import { auth } from "../firebase";

// Right after a sign-in (especially email-link activation) the freshly-minted
// ID token hasn't always propagated to the Firestore backend yet, so the very
// first writes can come back as "permission-denied" even though the user is
// fully authenticated and the security rules are correct. This wrapper retries
// such writes a few times, forcing an ID-token refresh between attempts so the
// SDK re-sends a credential the backend recognizes.
//
// Usage:
//   await authedWrite(() => setDoc(ref, data));
//
// Non-permission errors (validation, network, etc.) are re-thrown immediately
// so genuine failures aren't masked.
const MAX_ATTEMPTS = 4;
const BASE_DELAY_MS = 600;

function isPermissionDenied(err) {
  const code = err?.code || "";
  return (
    code === "permission-denied" ||
    code === "firestore/permission-denied" ||
    /insufficient permissions/i.test(err?.message || "")
  );
}

export async function authedWrite(fn, label = "write") {
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isPermissionDenied(err)) {
        throw err;
      }
      const cur = auth.currentUser;
      // Surface exactly why a permission-denied happened: a missing Firestore
      // credential (web SDK signed out) and a uid we can compare against the
      // path are the two real-world causes that a blind retry can never fix.
      console.warn(
        `[authedWrite] permission-denied on ${label} (attempt ${attempt}/${MAX_ATTEMPTS}) ` +
          `— auth.currentUser=${cur ? cur.uid : "null"} emailVerified=${cur ? cur.emailVerified : "n/a"}`
      );
      if (attempt === MAX_ATTEMPTS) {
        throw err;
      }
      // Force the SDK to mint and re-send a fresh credential, then back off
      // before retrying so the backend has time to recognize it.
      try {
        await cur?.getIdToken(true);
      } catch {
        /* token refresh best-effort; the delay + retry may still succeed */
      }
      await new Promise((resolve) => setTimeout(resolve, BASE_DELAY_MS * attempt));
    }
  }
  throw lastErr;
}

// Read equivalent of authedWrite: one-shot reads (getDoc/getDocs) that race
// ahead of the freshly-minted auth credential right after sign-in/enrollment
// can also come back permission-denied. Unlike onSnapshot listeners, one-shot
// reads do NOT auto-recover when the credential settles, so they need the same
// token-refresh + backoff retry. Returns whatever `fn` resolves to.
export async function authedRead(fn, label = "read") {
  return authedWrite(fn, label);
}
